import EventBus from '_lib/EventBus';
import { enterChannelByInviteCode } from '_api/invite';
import { checkIsInviteCode } from '_lib/utils/utils';
import { reportData } from '_api/report';
import Vue from 'vue';
import {
  DISCOVER_ENTER_WAY,
  ENTER_BY_INVITE_CODE_TYPES,
} from './constants/room';
import Cache from '_lib/DataCache';
import { getBlockQueryConfig } from './utils/utilsRoom';

/**
 * @description 通过邀请码或者邀请链接进入房间
 * @param {object} options
 * @param {string} options.invite_code 邀请码
 * @param {string} options.invite_url 邀请链接
 * @param {function} options.cb 成功回调
 * @param {function} options.error_cb 失败回调
 * @param {boolean} options.show_toast 是否显示toast
 * @param {boolean} options.only_text 进入频道是否仅文字聊天
 * @param {boolean} options.enterWay 进入的方式，数据埋点
 */
export const jumpRoomByCode = async function ({
  invite_code,
  invite_url,
  cb = null,
  error_cb = null,
  show_toast = true,
  only_text = false,
  enterWay = '',
}) {
  if (invite_url && !invite_code) {
    invite_code = await checkIsInviteCode(invite_url);
  }
  try {
    const successCb = (res) => {
      cb && cb();
    };
    const failedCb = (res) => {
      if (error_cb) {
        error_cb();
        return;
      }
    };
    handleEnterRoom(invite_code, ENTER_BY_INVITE_CODE_TYPES.friend, {
      successCb,
      failedCb,
      only_text,
      enterWay,
      show_toast,
    });
  } catch (e) {
    error_cb && error_cb();
  }
};

/**
 * @description 封装好的获取房间权限的方法
 * @description 通过邀请码发送请求到黑盒语音后端 /invite/code/use 接口，获取对应房间的权限
 * @description 部分房间会设置房间验证，需要用户输入验证码才能进入
 * @param {string} invite_code 邀请码，一般都是后端传回来的验证码
 * @param {number} type 区分进入方式 具体在 ENTER_BY_INVITE_CODE_TYPES 这个map里
 * @param {object} options 使用本函数的相关函数
 * @param {function} options.successCb 成功回调
 * @param {function} options.failedCb 失败回调
 * @param {function} options.cancelVerifyCb 取消验证回调
 * @param {boolean} options.only_text 进入频道是否仅文字聊天
 */
export const handleEnterRoom = async function (
  invite_code,
  type,
  {
    successCb,
    failedCb,
    cancelVerifyCb,
    only_text,
    enterWay,
    show_toast = true,
  } = {}
) {
  if (window.teamCenterAPI) {
    window.teamCenterAPI.jumpToRoom(invite_code);
    successCb && successCb();
    return;
  }
  let res = await handleSendEnterRoomRequest(
    {
      _notip: !show_toast,
    },
    {
      invite_code,
      type: getEnterWayType(enterWay) || type,
    }
  );
  res = res.data || res;
  const {
    status,
    result: { status_info, room_id, channel_id, is_guidance },
  } = res;
  if (status === 'ok') {
    if (!status_info) {
      successCb && successCb(res);
      reportEnterRoom(enterWay);
      await clearIsVisitor(room_id);
      EventBus.$jump({ room_id, channel_id, only_text, is_guidance });
      return;
    }
    const { status: resultStatus, room_join_verify } = status_info;
    if (resultStatus === 1) {
      const isNeedApply = room_join_verify.need_apply;
      const confirmCb = async ({ form: { answer, imgs } }) => {
        const data = {
          invite_code,
          type: getEnterWayType(enterWay) || type,
        };
        if (room_join_verify.need_apply) {
          data['reason'] = answer;
          data['imgs'] = imgs;
        } else {
          data['answer'] = answer;
        }
        const secondRes = await handleSendEnterRoomRequest({}, data);
        if (
          secondRes.data.status === 'ok' &&
          secondRes.data.result.status_info === null
        ) {
          successCb &&
            successCb(secondRes, {
              status: isNeedApply ? 'APPLIED' : 'VERIFIED',
            });
          reportEnterRoom(enterWay);
          await clearIsVisitor(secondRes.data.result.room_id);
          if (!isNeedApply) {
            EventBus.$jump({
              room_id: secondRes.data.result.room_id,
              channel_id: secondRes.data.result.channel_id,
              is_guidance: secondRes.data.result.is_guidance,
              only_text,
            });
          }
          return;
        }
        failedCb && failedCb(secondRes);
        if (secondRes.data.status === 'block_room') {
          const blockConfig = getBlockQueryConfig(secondRes.data);
          if (blockConfig) {
            Vue.prototype.$query(blockConfig);
          }
        }
      };
      const cancelCb = () => {
        cancelVerifyCb && cancelVerifyCb();
        return;
      };
      EventBus.$dynamic('RoomVerification', {
        verifyOptions: room_join_verify,
        confirmCb,
        cancelCb,
      });
    }
  } else {
    failedCb && failedCb(res);
    if (status === 'block_room') {
      const blockConfig = getBlockQueryConfig(res);
      if (blockConfig) {
        Vue.prototype.$query(blockConfig);
      }
    }
  }
};

export const reportEnterRoom = function (enterWay) {
  if (!enterWay) return;
  reportData('enter-room', {
    type: enterWay,
  });
};

// 根据EnterWay获取邀请码使用的Type
export const getEnterWayType = function (enterWay) {
  switch (enterWay) {
    case 'apply': // 邀请页
      return ENTER_BY_INVITE_CODE_TYPES.friend;
    case 'invite': // 邀请页
      return ENTER_BY_INVITE_CODE_TYPES.friend;
    case 'friend-invite': // 好友直接邀请
      return ENTER_BY_INVITE_CODE_TYPES.friend;
    case DISCOVER_ENTER_WAY.RECOMMEND:
      return ENTER_BY_INVITE_CODE_TYPES.recommend;
    case DISCOVER_ENTER_WAY.HOT:
      return ENTER_BY_INVITE_CODE_TYPES.hot;
    case DISCOVER_ENTER_WAY.SEARCH_STRIGHT:
      return ENTER_BY_INVITE_CODE_TYPES.search_straight;
    case DISCOVER_ENTER_WAY.SEARCH_BLURRY:
      return ENTER_BY_INVITE_CODE_TYPES.search;
    case 'friend-list-follow': // 好友列表跟随
      return ENTER_BY_INVITE_CODE_TYPES.follow;
    case DISCOVER_ENTER_WAY.TEAM_INVITE:
      return ENTER_BY_INVITE_CODE_TYPES.team_invite;
    case DISCOVER_ENTER_WAY.IM:
      return ENTER_BY_INVITE_CODE_TYPES.channel_im;
    case DISCOVER_ENTER_WAY.GROUP_IM:
      return ENTER_BY_INVITE_CODE_TYPES.group_im;
    case DISCOVER_ENTER_WAY.PRIVATE_IM:
      return ENTER_BY_INVITE_CODE_TYPES.private_im;
    default:
      return undefined;
  }
};

// 清除用户游客状态 避免用户在房间内的权限计算出问题
const clearIsVisitor = async function (room_id) {
  const cache_data = await Cache.getRoomCache(room_id);
  if (cache_data) {
    let { my_room_info } = cache_data;
    if (my_room_info && my_room_info.is_visitor) {
      Vue.set(my_room_info, 'is_visitor', false);
    }
    Cache.setRoomCache({
      id: room_id,
      data: cache_data,
    });
  }
};

/**
 * 发送进入房间请求
 * @description 尽量不要使用这个函数， 直接使用handleEnterRoom函数
 * @param {object} params 请求参数
 * @param {object} body 请求体
 * @returns {Promise<object>} 请求结果
 */
export async function handleSendEnterRoomRequest(params, body) {
  let res = await enterChannelByInviteCode(params, body);
  return res;
}
